const SW_URL = '/sw.js';

/** Origen canónico para instalar la PWA (no usar localhost). */
export const PRODUCTION_PWA_ORIGIN = 'https://contsuite.com.ar';

export const PWA_INSTALL_DISMISS_KEY = 'contsuite_pwa_desktop_install_dismissed_v1';
export const PWA_INSTALLED_KEY = 'contsuite_pwa_installed_v1';
/** Altura del aviso localhost (MarketingLayout desplaza la navbar). */
export const LOCALHOST_PWA_BANNER_CSS_VAR = '--contsuite-localhost-pwa-banner-height';
export const LOCALHOST_PWA_WARNING_DISMISS_KEY = 'contsuite_localhost_pwa_warning_dismissed_session';

/** Pasos para instalar desde el navegador (cuando no hay botón automático). */
export const PWA_INSTALL_STEPS = [
  'Abrí Contsuite en Chrome o Microsoft Edge en esta computadora.',
  'En la barra de direcciones, buscá el ícono “Instalar” (⊕ o un monitor con flecha).',
  'Si no lo ves: menú ⋮ (arriba a la derecha) → “Instalar Contsuite” o “Instalar aplicación”.',
  'Confirmá. Contsuite aparecerá en el menú Inicio de Windows o en Aplicaciones en Mac.',
];

export const PWA_INSTALL_HINT = PWA_INSTALL_STEPS.join(' ');

export function markPwaInstalled() {
  try {
    localStorage.setItem(PWA_INSTALLED_KEY, '1');
  } catch {
    /* ignore */
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('contsuite:pwa-installed'));
  }
}

export function clearPwaInstallDismissed() {
  try {
    localStorage.removeItem(PWA_INSTALL_DISMISS_KEY);
  } catch {
    /* ignore */
  }
}

const DISPLAY_MODES_INSTALLED = ['standalone', 'minimal-ui', 'fullscreen', 'window-controls-overlay'];

export function isStandalonePwa() {
  if (typeof window === 'undefined') return false;
  if (window.navigator.standalone === true) return true;
  return DISPLAY_MODES_INSTALLED.some((mode) =>
    window.matchMedia(`(display-mode: ${mode})`).matches
  );
}

/** Pestaña normal de Chrome/Edge (no ventana de la app instalada). */
export function isExplicitBrowserTab() {
  if (typeof window === 'undefined') return true;
  try {
    return window.matchMedia('(display-mode: browser)').matches;
  } catch {
    return true;
  }
}

/** Ventana de la app instalada (aunque display-mode no reporte standalone). */
export function isRunningAsInstalledClient() {
  if (isStandalonePwa()) return true;
  if (!isDesktopPwaTarget()) return false;
  return !isExplicitBrowserTab();
}

function readLocalInstalledMark() {
  try {
    return localStorage.getItem(PWA_INSTALLED_KEY) === '1';
  } catch {
    return false;
  }
}

/** Si la PWA está instalada en el equipo pero esta pestaña es el navegador. */
export async function isPwaInstalledOnDevice() {
  if (isStandalonePwa()) return true;
  if (readLocalInstalledMark()) return true;
  if (typeof navigator !== 'undefined' && 'getInstalledRelatedApps' in navigator) {
    try {
      const related = await navigator.getInstalledRelatedApps();
      if (Array.isArray(related) && related.length > 0) return true;
    } catch {
      /* API no disponible o denegada */
    }
  }
  return false;
}

/**
 * Estado para UI de Cuenta / instalación.
 * - running-app: esta ventana ES la app instalada
 * - installed: instalada en el PC, pero estás en una pestaña del navegador
 * - not-installed: no detectada
 * - mobile: no aplica instalación de escritorio
 */
export async function getDesktopPwaInstallState() {
  if (!isDesktopPwaTarget()) {
    return { kind: 'mobile' };
  }
  if (isLocalDevOrigin()) {
    if (isRunningAsInstalledClient()) {
      return { kind: 'local-dev-installed', productionUrl: getProductionPwaUrl('/login') };
    }
    return { kind: 'local-dev', productionUrl: getProductionPwaUrl('/login') };
  }
  if (isRunningAsInstalledClient()) {
    markPwaInstalled();
    return { kind: 'running-app' };
  }
  const installed = await isPwaInstalledOnDevice();
  if (installed) {
    return { kind: 'installed' };
  }
  return { kind: 'not-installed', canPrompt: canShowInstallPrompt() };
}

const MOBILE_UA_RE = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i;

export function isLocalDevOrigin() {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname.toLowerCase();
  return h === 'localhost' || h === '127.0.0.1';
}

/** Dominio donde la PWA puede hablar con la API sin servidores locales. */
export function isProductionPwaOrigin() {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname.toLowerCase();
  if (h === 'contsuite.com.ar' || h === 'www.contsuite.com.ar') return true;
  if (h.endsWith('.vercel.app')) return true;
  return false;
}

export function getProductionPwaUrl(path = '/inicio') {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${PRODUCTION_PWA_ORIGIN}${normalized}`;
}

/** La instalación de escritorio solo tiene sentido en prod (no en npm start). */
export function shouldOfferPwaInstall() {
  return isDesktopPwaTarget() && isProductionPwaOrigin() && !isLocalDevOrigin();
}

/** PWA orientada a escritorio (Chrome/Edge en Windows, macOS, Linux). */
export function isDesktopPwaTarget() {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (MOBILE_UA_RE.test(ua)) return false;
  return window.matchMedia('(min-width: 1024px)').matches;
}

let deferredInstallPrompt = null;
let refreshing = false;

export function captureInstallPrompt() {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredInstallPrompt = e;
    window.dispatchEvent(new CustomEvent('contsuite:pwa-installable'));
  });
  window.addEventListener('appinstalled', () => {
    markPwaInstalled();
  });
}

export function canShowInstallPrompt() {
  if (!shouldOfferPwaInstall()) return false;
  return Boolean(deferredInstallPrompt);
}

export async function promptPwaInstall() {
  if (!deferredInstallPrompt) return { outcome: 'unavailable' };
  deferredInstallPrompt.prompt();
  const outcome = await deferredInstallPrompt.userChoice;
  if (outcome.outcome === 'accepted') {
    deferredInstallPrompt = null;
    clearPwaInstallDismissed();
    markPwaInstalled();
  }
  return outcome;
}

function watchServiceWorkerUpdates(registration) {
  if (!registration) return;

  registration.addEventListener('updatefound', () => {
    const worker = registration.installing;
    if (!worker) return;
    worker.addEventListener('statechange', () => {
      if (worker.state === 'installed' && navigator.serviceWorker.controller) {
        window.dispatchEvent(new CustomEvent('contsuite:pwa-update'));
      }
    });
  });

  if (registration.waiting && navigator.serviceWorker.controller) {
    window.dispatchEvent(new CustomEvent('contsuite:pwa-update'));
  }
}

export function applyPwaUpdate() {
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.getRegistration().then((reg) => {
    reg?.waiting?.postMessage({ type: 'SKIP_WAITING' });
  });
}

export async function unregisterServiceWorkers() {
  if (!('serviceWorker' in navigator)) return;
  const regs = await navigator.serviceWorker.getRegistrations();
  await Promise.all(regs.map((r) => r.unregister()));
}

export function registerServiceWorker() {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;

  if (process.env.NODE_ENV !== 'production') {
    unregisterServiceWorkers();
    return;
  }

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing) return;
    refreshing = true;
    window.location.reload();
  });

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register(SW_URL, { scope: '/' })
      .then((reg) => watchServiceWorkerUpdates(reg))
      .catch((err) => {
        console.warn('[Contsuite] No se pudo registrar el service worker:', err);
      });
  });
}
